import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { TenantProvider, useTenant } from './context/TenantContext';
import { EntityProvider } from './context/EntityContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { ToastProvider } from './components/ui';
import { PublicLayout } from './components/layout/PublicLayout';
import { AdminLayout } from './components/layout/AdminLayout';

import { SimpleHomePage } from './pages/public/SimpleHomePage';
import { JobsPage } from './pages/public/JobsPage';
import { JobDetailPage } from './pages/public/JobDetailPage';
import { LoginPage } from './pages/admin/LoginPage';
import { SimpleDashboard } from './pages/admin/SimpleDashboard';
import { PostJobPage } from './pages/admin/PostJobPage';
import { JobManagementPage } from './pages/admin/JobManagementPage';
import { ApplicationsPage } from './pages/admin/ApplicationsPage';
import { ApplicationDetailPage } from './pages/admin/ApplicationDetailPage';
import { ProfilePage } from './pages/admin/ProfilePage';
import { SettingsPage } from './pages/admin/SettingsPage';
import { CallScreeningPage } from './pages/admin/CallScreeningPage';
import { CallLogsPage } from './pages/admin/CallLogsPage';
import { BulkResumeMatchingPage } from './pages/admin/BulkResumeMatchingPage';
import { SuperAdminPage } from './pages/admin/SuperAdminPage';
import { AnalyticsPage } from './pages/admin/AnalyticsPage';
import { NotificationsPage } from './pages/admin/NotificationsPage';
import { EntityAnalyticsPage } from './pages/admin/EntityAnalyticsPage';
import { theme, createTenantTheme } from './theme/theme';

const queryClient = new QueryClient();

const AppContent: React.FC = () => {
  const { currentTenant } = useTenant();

  // Use custom theme if:
  // 1. Tenant exists
  // 2. customThemes feature is enabled (not explicitly false)
  // 3. Theme colors are defined
  const shouldUseCustomTheme = currentTenant &&
    currentTenant.features?.customThemes !== false &&
    currentTenant.theme?.primaryColor;

  const appTheme = shouldUseCustomTheme
    ? createTenantTheme({
      primaryColor: currentTenant.theme.primaryColor,
      secondaryColor: currentTenant.theme.secondaryColor,
    })
    : theme;

  // Default feature flags if no tenant
  const defaultFeatures = {
    resumeScreening: true,
    aiScreening: true,
    analytics: true,
    mockInterviews: true,
    callInitiation: true,
    customThemes: true
  };

  return (
    <ThemeProvider theme={appTheme}>
      <CssBaseline />
      <ToastProvider>
        <EntityProvider mode="auto">
          <FeatureFlagProvider features={currentTenant?.features || defaultFeatures}>
            <Router>
              <Routes>
              {/* Public Routes */}
              <Route path="/" element={
                <PublicLayout>
                  <SimpleHomePage />
                </PublicLayout>
              } />
              <Route path="/jobs" element={
                <PublicLayout>
                  <JobsPage />
                </PublicLayout>
              } />
              <Route path="/jobs/:id" element={
                <PublicLayout>
                  <JobDetailPage />
                </PublicLayout>
              } />

              {/* Admin Routes */}
              <Route path="/admin/login" element={<LoginPage />} />
              <Route path="/admin/dashboard" element={
                <AdminLayout>
                  <SimpleDashboard />
                </AdminLayout>
              } />
              <Route path="/admin/jobs" element={
                <AdminLayout>
                  <JobManagementPage />
                </AdminLayout>
              } />
              <Route path="/admin/jobs/post" element={
                <AdminLayout>
                  <PostJobPage />
                </AdminLayout>
              } />
              <Route path="/admin/jobs/edit/:id" element={
                <AdminLayout>
                  <PostJobPage />
                </AdminLayout>
              } />
              <Route path="/admin/applications" element={
                <AdminLayout>
                  <ApplicationsPage />
                </AdminLayout>
              } />
              <Route path="/admin/applications/:id" element={
                <AdminLayout>
                  <ApplicationDetailPage />
                </AdminLayout>
              } />
              <Route path="/admin/bulk-matching" element={
                <AdminLayout>
                  <BulkResumeMatchingPage />
                </AdminLayout>
              } />
              <Route path="/admin/analytics" element={
                <AdminLayout>
                  <AnalyticsPage />
                </AdminLayout>
              } />
              <Route path="/admin/interviews" element={
                <AdminLayout>
                  <div>Mock Interviews (Coming Soon)</div>
                </AdminLayout>
              } />
              <Route path="/admin/calls" element={
                <AdminLayout>
                  <CallScreeningPage />
                </AdminLayout>
              } />
              <Route path="/admin/call-logs" element={
                <AdminLayout>
                  <CallLogsPage />
                </AdminLayout>
              } />
              <Route path="/admin/settings" element={
                <AdminLayout>
                  <SettingsPage />
                </AdminLayout>
              } />
              <Route path="/admin/profile" element={
                <AdminLayout>
                  <ProfilePage />
                </AdminLayout>
              } />
              <Route path="/admin/super-admin" element={
                <AdminLayout>
                  <SuperAdminPage />
                </AdminLayout>
              } />
              <Route path="/admin/notifications" element={
                <AdminLayout>
                  <NotificationsPage />
                </AdminLayout>
              } />
              <Route path="/admin/entity-analytics" element={
                <AdminLayout>
                  <EntityAnalyticsPage />
                </AdminLayout>
              } />
              </Routes>
            </Router>
          </FeatureFlagProvider>
        </EntityProvider>
      </ToastProvider>
    </ThemeProvider>
  );
};
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <AppContent />
      </TenantProvider>
    </QueryClientProvider>
  );
}

export default App;
